<template>
  <div>
    <v-card>
      <system-configuration-tabs current-tab="ManagePrograms" />
      <v-toolbar flat color="white" class="mt-4">
        <v-toolbar-title>Manage Programs</v-toolbar-title>
        <v-spacer />
        <search-filter v-model="keywordSearch" />
        <v-btn color="secondary" dark @click="dialog = true">
          Add
        </v-btn>
      </v-toolbar>
      <v-data-table :headers="headers" :items="programs" :pagination.sync="pagination" :total-items="totalRecords" :loading="loading" :rows-per-page-items="rowsPerPageItems">
        <v-progress-linear slot="progress" color="secondary" indeterminate />
        <template slot="items" slot-scope="props">
          <td>
            <img v-if="props.item.iconUrl" style="width:30px; height:30px" :src="props.item.iconUrl">
            <img v-else style="width:30px; height:30px" src="https://via.placeholder.com/150?text=N/A">
          </td>
          <td>
            <div :style="{ backgroundColor: props.item.colorCode }" style="min-height:20px; width:20px" />
          </td>
          <td>{{ props.item.name }}</td>
          <td class="text-xs-right">
            <v-icon small class="mr-2" @click="editModal(props.item)">
              edit
            </v-icon>
          </td>
          <td class="text-xs-right">
            <v-icon @click="deleteProgram(props.item)">
              far fa-trash-alt
            </v-icon>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" content-class="programDialog" max-width="500px" persistent>
      <v-card>
        <form @submit.prevent="save">
          <v-card-title>
            <span class="headline">
              {{ editedItem.id ? 'Edit' : 'Add' }} Program
            </span>
          </v-card-title>
          <alert-banner :msg="alertMessage" />
          <v-card-text>
            <v-container fluid mt-3>
              <v-layout wrap row justify-content-center>
                <v-flex xs11>
                  <v-text-field v-model="editedItem.name" label="Program Name" required />
                </v-flex>
                <v-flex xs11>
                  <v-layout>
                    <v-flex>
                      <p class="mb-0">
                        Select a Background Color
                      </p>
                      <p class="caption">
                        Seen on the Curriculum Listing Page
                      </p>
                    </v-flex>
                    <v-flex class="text-center">
                      <swatches v-model="editedItem.colorCode" colors="material-basic" show-fallback popover-to="left" />
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex xs11 class="mt-2">
                  <v-layout>
                    <v-flex align-self-center grow>
                      <p class="mb-0">
                        Select a Program Icon
                      </p>
                      <p class="caption">
                        Seen on the Curriculum Listing Page
                      </p>
                    </v-flex>
                    <v-flex class="text-center xs3 pr-3">
                      <image-input v-model="editedItem.imageFile" :original-image="editedItem.iconUrl" />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn flat @click.native="close">
              Cancel
            </v-btn>
            <v-btn type="submit" color="secondary darken-1" flat :loading="saving">
              Save changes
            </v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
  import _ from 'lodash';
  import localforage from 'localforage';
  import axios from 'axios';
  import ImageInput from '~/Resources/scripts/patterns/ImageInputWithCropper.vue';
  import Swatches from 'vue-swatches';
  import 'vue-swatches/dist/vue-swatches.min.css';
  import SystemConfigurationTabs from './components/SystemConfigurationTabs.vue';

  export default {
    parent: true,

    components: {
      ImageInput,
      SystemConfigurationTabs,
      Swatches,
    },

    data: () => ({
      dialog: false,
      loading: true,
      programs: [],
      saving: false,
      defaultItem: {
        name: '',
        imageFile: null,
        iconUrl: null,
        colorCode: '#dc3545',
      },
      editedItem: {
        name: '',
        imageFile: null,
        iconUrl: null,
        colorCode: '#dc3545',
      },
      totalRecords: 0,
      rowsPerPageItems: [20, 50, 75, 100],
      pagination: {
        descending: false, page: 1, rowsPerPage: 20, sortBy: 'name',
      },
      headers: [
        {
          text: 'Icon', value: 'iconUrl', align: 'left', sortable: false,
        },
        {
          text: 'Color', value: 'name', align: 'left', sortable: true,
        },
        {
          text: 'Name', value: 'name', align: 'left', sortable: true,
        },
        { text: 'Edit', align: 'right', sortable: false },
        { text: 'Delete', align: 'right', sortable: true },
      ],
      keywordSearch: '',
      addDialog: false,
    }),

    watch: {
      pagination: {
        handler(newval, oldval) {
          if (!_.isEqual(newval, oldval)) {
            this.getPrograms();
          }
        },
        deep: true,
      },

      'pagination.rowsPerPage': function (val) {
        localforage.setItem('ManageProgramsGrid:RowsPerPage', val);
      },

      keywordSearch() {
        this.processSearch();
      },

      dialog(val) {
        val || this.close();
      },
    },

    mounted() {
      localforage.getItem('ManageProgramsGrid:RowsPerPage').then((value) => {
        this.pagination.rowsPerPage = value || 20;
        this.getPrograms();
      });
    },

    methods: {
      async getPrograms() {
        const {
          sortBy, descending, page, rowsPerPage,
        } = this.pagination;
        const sortDirection = descending ? `${sortBy} DESC` : `${sortBy} ASC`;
        this.loading = true;
        const { data } = await axios.get(`/api/programs/paged?currentPage=${page}&pageSize=${rowsPerPage}&sortDir=${sortDirection}&filter=${this.keywordSearch}`);
        this.programs = data.resultItems;
        this.totalRecords = data.totalRecords;
        this.loading = false;
      },
      async save() {
        this.saving = true;
        if (this.editedItem.id) {
          await axios.patch(`/api/programs/${this.editedItem.id}`, this.editedItem)
            .then(async () => {
              if (this.editedItem.imageFile) {
                await axios.put(`/api/programs/${this.editedItem.id}/image`, this.editedItem.imageFile.formData);
              }
              this.getPrograms().then(() => {
                this.dialog = false;
              });
            })
            .catch((error) => {
              this.processErrors(error, this.editedItem);
            })
            .then(() => {
              this.saving = false;
            });
        } else {
          await axios.post('/api/programs/', this.editedItem)
            .then(async (data) => {
              if (this.editedItem.imageFile) {
                await axios.put(`/api/programs/${data.data.id}/image`, this.editedItem.imageFile.formData);
              }
              this.getPrograms().then(() => {
                this.dialog = false;
              });
            })
            .catch((error) => {
              this.processErrors(error, this.editedItem);
            })
            .then(() => {
              this.saving = false;
            });
        }
      },
      async deleteProgram(program) {
        const result = confirm('Are you sure you want to delete this item?'); // eslint-disable-line
        if (result) {
          await axios.delete(`/api/programs/${program.id}`);
          await this.getPrograms();
        }
      },
      close() {
        this.dialog = false;
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem);
        }, 300);
      },
      editModal(item) {
        this.editedItem = item;
        this.dialog = true;
      },

      processSearch: _.debounce(function () {
        this.getPrograms();
      }, 600),
    },
  };
</script>

<style>
.programDialog.v-dialog {
  overflow-y: inherit;
}
</style>